import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { shareReplay, tap, takeUntil, catchError } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { TranscriptSource } from '../shared/models/transcript-source.model';
import { TranscriptProcessStatus } from '../shared/models/transcript-process-status.model';
import { Transcript } from '../shared/models/transcript';
import { TranscriptType } from '../shared/models/transcript-type';
import { Observable, merge, fromEvent, of } from 'rxjs';
import { GradeKey } from '../shared/models/grade-key.model';
import { SubjectRecord } from '../shared/models/subject-record.model';
import { Result } from '../shared/models/result.model';
import { TranscriptSubject } from '../shared/models/transcript-subject.model';
import { Education } from '../shared/models/education.model';
import { FinishTranscriptProcessingRequest } from '../shared/models/finish-transcript-processing-request.model';


export interface UpdateTranscriptRequest {
    educationId: number;
    transcriptProcessStatusId: number;
    transcriptTypeId: number;
    modUser: string;
    profileStateId: number;
}

@Injectable({
    providedIn: 'root'
})
export class TranscriptService {
    headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    constructor(private httpClient: HttpClient,
        private configService: ConfigService
    ) {
    }

    public getTranscriptSources() {
        return this.httpClient.get<TranscriptSource[]>(`${this.configService.apiUrl}/transcript/source`).pipe(
            shareReplay()
        );
    }

    public getTranscriptTypes() {
        return this.httpClient.get<TranscriptType[]>(`${this.configService.apiUrl}/transcript/types`).pipe(
            shareReplay()
        );
    }

    public upload(trascriptFile: File, transcriptSourceID: number, uploadUserName: string) {
        const formData = new FormData();
        formData.append("SubmitterName", uploadUserName);
        formData.append("TranscriptSourceId", <string><unknown>transcriptSourceID);
        formData.append("file", trascriptFile, trascriptFile.name);
        const formHeaders = new HttpHeaders();
        formHeaders.set('Content-Type', 'multipart/form-data');
        return this.httpClient.post(`${this.configService.apiUrl}/transcript`, formData, { headers: formHeaders });
    }

    public getTranscriptStatuses() {
        return this.httpClient.get<TranscriptProcessStatus[]>(`${this.configService.apiUrl}/transcript/process-status`).pipe(
            shareReplay()
        );
    }

    public getTranscripts(
        programStateIDs: string,
        transcriptSourceID: string,
        transcriptStatusID: string,
        transcriptTypeIDs: number[],
        companyIDs: number[],
        uploadStartDate: Date,
        uploadEndDate: Date,
        candidateName?: string,
        institutionName?: string
    ) {

        let queryParameters: { [index: string]: any; } = {};
        queryParameters['stateids'] = programStateIDs;
        queryParameters['srcid'] = transcriptSourceID;
        queryParameters['statid'] = transcriptStatusID;
        queryParameters['typeids'] = transcriptTypeIDs.toString();
        queryParameters['companyids'] = companyIDs.toString();
        queryParameters['ulstart'] = !!uploadStartDate ? uploadStartDate.toISOString() : '';
        queryParameters['ulend'] = !!uploadEndDate ? uploadEndDate.toISOString() : '';
        if (candidateName) {
            queryParameters['candidateName'] = candidateName;
        }
        if (institutionName) {
            queryParameters['institutionName'] = institutionName;
        }

        let queryParams = '';
        for (let key in queryParameters) {
            let value = queryParameters[key];
            if (value !== '' && value !== '0') {
                if (queryParams.length > 0) {
                    queryParams += '&'
                }
                queryParams += key + '=' + encodeURI(value);
            }
        }

        if (queryParams.length > 0) {
            queryParams = '?' + queryParams;
        }

        return this.httpClient.get<Transcript[]>(`${this.configService.apiUrl}/transcript${queryParams}`);
    }

    public downloadTranscript(id: number) {
        const headers = new HttpHeaders({ 'Accept': 'application/pdf' });
        return this.httpClient.get(this.configService.apiUrl + '/transcript/' + id + '?type=pdf', { headers: headers, responseType: 'blob' });
    }

    public updateTranscript(id: number, request: UpdateTranscriptRequest) {

        return this.httpClient.put(`${this.configService.apiUrl}/transcript/${id}`, request);
    }

    openTranscriptPopup(id: number, closeNotifier: Observable<any>) {

        return this.downloadTranscript(id)
            .pipe(tap(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const popoutWindow = window.open(
                    '',
                    `_blank`,
                    `width=${document.body.clientWidth * 0.50},height=${document.body.clientHeight * 0.70},left=${20},top=${20}`);

                popoutWindow.document.title = 'Advisor'
                popoutWindow.document.body.innerHTML = `<iframe src="${blobUrl}" style="width: 100%; height: calc(100% - 4px);"></iframe>`
                popoutWindow.onbeforeunload = () => {
                    window.URL.revokeObjectURL(blobUrl);
                }
                merge(closeNotifier, fromEvent(window, 'beforeunload'))
                    .pipe(
                        takeUntil(fromEvent(popoutWindow, 'beforeunload'))
                    ).subscribe(() => popoutWindow.close());
            }));
    }

    public downloadFromURL(downloadURL: string) {
        this.headers = this.headers.set('Accept', 'application/pdf');
        return this.httpClient.get<any>(downloadURL, { headers: this.headers, responseType: 'blob' as 'json' });
    }

    public getGradeKeys() {
        return this.httpClient.get<GradeKey[]>
            (`${this.configService.apiUrl}/v1.0/grade-keys`).pipe(
                catchError(error => {
                    console.log(error);
                    return [];
                })
            );
    }

    public getSubjectRecordsByTranscriptId(transcriptId: number) {
        return this.httpClient.get<SubjectRecord[]>(`${this.configService.apiUrl}/v1.0/education-transcripts/${transcriptId}/subject-records`);
    }

    public updateSubjectRecordModifiedByTranscriptId(transcriptId: number, request: UpdateTranscriptRequest) {
        return this.httpClient.put(`${this.configService.apiUrl}/v1.0/education-transcripts/${transcriptId}/modified-subject-records`, request);
    }

    public createTranscriptSubjectRecords(request: SubjectRecord[] | SubjectRecord): Observable<Result> {
        return this.httpClient.post<Result>(`${this.configService.apiUrl}/v1.0/education-transcripts/subject-records`, request);
    }

    public updateTranscriptSubjectRecords(request: SubjectRecord[] | SubjectRecord): Observable<Result> {
        return this.httpClient.put<Result>(`${this.configService.apiUrl}/v1.0/education-transcripts/subject-records`, request);
    }

    public getTranscriptSubjects(stateId: number, companyId?: number) {
        const url = companyId != null
            ? `${this.configService.apiUrl}/v1.0/transcript-subjects?stateId=${stateId}&companyId=${companyId}`
            : `${this.configService.apiUrl}/v1.0/transcript-subjects?stateId=${stateId}`;
        return this.httpClient.get<TranscriptSubject[]>(url).pipe(
            shareReplay()
        );
    }

    public updateTranscriptSubjectCertArea(request: TranscriptSubject | TranscriptSubject[]) {
        return this.httpClient.put<Result>(`${this.configService.apiUrl}/v1.0/transcript-subjects`, request);
    }

    public finishTranscriptProcessing(request: FinishTranscriptProcessingRequest, transcriptId: number) {
        return this.httpClient.post(`${this.configService.apiUrl}/v1.0/educations/${request.educationId}/transcripts/${transcriptId}`, request);
    }
}
